// Centralized Pricing Configuration for Camyvera
// All pricing plans and minutes calculations in one place

export const PRICING_PLANS = [
  {
    id: "Free",
    name: "Free Plan",
    price: "$0 / Month",
    priceValue: 0,
    hours: "300 minutes/month",
    minutes: 300,
    firstMonthMinutes: 300,
    description: "Free forever. 300 minutes every month.",
    features: [
      "300 minutes every month — forever",
      "Audio & Video calls included",
      "High-quality audio and video",
      "Instant access to all mavens",
      "No scheduling required — connect when available",
      "Basic call summaries included",
    ],
    cta: "Start Free Plan",
  },
  {
    id: "starter_monthly",
    name: "Starter Plan",
    price: "$14.99 / Month",
    priceValue: 14.99,
    hours: "600 minutes included",
    minutes: 600,
    description: "Perfect for occasional users.",
    features: [
      "600 maven call minutes per month",
      "Audio & Video calls included",
      "High-quality video and audio",
      "Smart matching across all mavens",
      "Call summaries available",
      "Faster support response",
    ],
    cta: "Choose Plan",
  },
  {
    id: "pro_monthly",
    name: "Professional Plan",
    price: "$49.99 / Month",
    priceValue: 49.99,
    hours: "2500 minutes included",
    minutes: 2500,
    description: "Best value for regular users.",
    popular: true,
    features: [
      "2500 maven call minutes monthly",
      "Audio & Video calls included",
      "HD video quality",
      "Advanced matching and discovery controls",
      "Detailed call summaries and notes",
      "Priority support & faster matchmaking",
    ],
    cta: "Choose Plan",
  },
  {
    id: "unlimited_monthly",
    name: "Unlimited Plan",
    price: "$99.99 / Month",
    priceValue: 99.99,
    hours: "Unlimited minutes",
    minutes: 999999, // Effectively unlimited
    description: "For power users - no limits.",
    features: [
      "Unlimited maven call time for individual use",
      "Audio & Video calls included",
      "Premium HD video quality",
      "Dedicated success manager & calendar integrations",
      "Advanced analytics and call insights",
      "Priority support with the fastest response SLA",
    ],
    cta: "Choose Plan",
  },
];

// Helper function to get plan by ID
export function getPlanById(planId) {
  const normalizedId = planId?.toLowerCase() || 'free';
  return PRICING_PLANS.find(p => p.id.toLowerCase() === normalizedId) || PRICING_PLANS[0];
}

// Helper function to get monthly minutes for a plan
export function getMonthlyMinutes(planId) {
  const plan = getPlanById(planId);
  return plan.minutes;
}

// Helper function to calculate call costs
export function calculateCallCost(durationMinutes, expertRatePerMinute) {
  const MINIMUM_FEE = 0.30;
  const MINIMUM_FEE_THRESHOLD = 10; // minutes
  const FREE_FIRST_MINUTE = 0;

  // Billing starts immediately
  const billableMinutes = Math.max(0, durationMinutes - FREE_FIRST_MINUTE);

  // Calculate base cost
  const baseCost = billableMinutes * expertRatePerMinute;

  // Apply minimum fee for calls under threshold
  if (durationMinutes < MINIMUM_FEE_THRESHOLD && durationMinutes > FREE_FIRST_MINUTE) {
    return MINIMUM_FEE + baseCost;
  }

  return baseCost;
}

// Helper function to format pricing example
export function getPricingExample(expertRate) {
  const shortCallMinutes = 3;
  const longCallMinutes = 15;

  const shortCallCost = calculateCallCost(shortCallMinutes, expertRate);
  const longCallCost = calculateCallCost(longCallMinutes, expertRate);

  return {
    shortCall: {
      minutes: shortCallMinutes,
      cost: shortCallCost.toFixed(2),
      breakdown: `$0.30 + $${(expertRate * shortCallMinutes).toFixed(2)} = $${shortCallCost.toFixed(2)}`
    },
    longCall: {
      minutes: longCallMinutes,
      cost: longCallCost.toFixed(2),
      breakdown: `$${(expertRate * longCallMinutes).toFixed(2)} (no minimum fee)`
    }
  };
}

export default PRICING_PLANS;
